// Static data API — replaces all backend calls with reads from /data/*.json
// and writes to localStorage. Designed for offline-first PWA.
//
// "Pregúntale a Papi" still requires a remote endpoint (Cloudflare Worker)
// because we need a live LLM call + voice synthesis. The base URL is configured
// via REACT_APP_PAPI_WORKER_URL.

import { applyKosherMode, transformOne, readKosherMode } from "./kosher";

let _cache = { recipes: null, meta: null, capsules: null, members: null };

async function loadJson(path) {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to load ${path}`);
  return res.json();
}

// Helper: kosher mode del usuario actual (miembro/invitado).
function currentKosherMode() {
  if (typeof localStorage === "undefined") return false;
  const member = currentMember();
  const guestName = localStorage.getItem("recetario_guest_name") || "";
  return readKosherMode(member, guestName);
}

async function getRecipes() {
  if (!_cache.recipes) _cache.recipes = await loadJson("/data/recipes.json");
  // Merge user-added recipes from localStorage (per member)
  const member = currentMember();
  let user = [];
  try { user = JSON.parse(localStorage.getItem(`recetario_user_recipes_${member}`) || "[]"); } catch { user = []; }
  const all = [...user, ..._cache.recipes];
  // Aplica el modo kosher si está activo — oculta lo prohibido y transforma
  const kosher = currentKosherMode();
  if (kosher) {
    const { visible } = applyKosherMode(all, true);
    return visible;
  }
  return all;
}

async function getMeta() {
  if (!_cache.meta) _cache.meta = await loadJson("/data/meta.json");
  return _cache.meta;
}

async function getCapsules() {
  if (!_cache.capsules) _cache.capsules = await loadJson("/data/capsules.json");
  return _cache.capsules;
}

async function getMembers() {
  if (!_cache.members) _cache.members = await loadJson("/data/members.json");
  return _cache.members;
}

// Turn the search/filter params (URLSearchParams or plain object) into a filter fn.
// Búsqueda tolerante a acentos, sin orden de palabras, ignorando conectores (de, la, con...)
// y reconociendo sinónimos chilenos (palta=aguacate, choclo=maiz, camote=batata) +
// plural simple (camarón ↔ camarones). Cada token relevante debe aparecer como
// substring del "heno" (nombre + descripción + ingredientes + tags + categoría...).
const _stripAccents = (s) => String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const _norm = (s) => _stripAccents(String(s).toLowerCase());
// Conectores que no deben contar como tokens obligatorios
const _STOP = new Set([
  "de","la","el","los","las","y","con","sin","en","a","al","del","un","una","unos","unas",
  "para","por","o","u","e","i","lo","su","sus","es","mi","tu","te","se","le","les",
  "que","como","mas","muy","ya"
]);
// Sinónimos chilenos / regionales — cualquiera de estos términos buscados
// también matchea sus equivalentes en el contenido.
const _SYNS = [
  ["palta","aguacate"],
  ["choclo","maiz","maíz","elote"],
  ["camote","batata","boniato"],
  ["porotos","frijoles","habichuelas","alubias"],
  ["zapallo","calabaza","ahuyama"],
  ["palomitas","cabritas","pochoclo"],
  ["tocino","panceta","bacon","beicon"],
  ["camaron","camarones","gamba","gambas","langostino","langostinos"],
  ["frutilla","fresa","frutillas","fresas"],
  ["durazno","melocoton","duraznos","melocotones"],
  ["damasco","albaricoque","damascos"],
  ["platano","banana","banano","cambur"],
  ["aji","chile","pimiento","pimenton"],
  ["pepino","pepinillo","pepinos"],
  // Carne de vacuno: cuando el user busca "carne", debe también matchear
  // recetas etiquetadas como "vacuno", "res" o cortes específicos.
  ["carne","vacuno","res","vaca","novillo","novillito"],
];
const _SYN_INDEX = (() => {
  const m = new Map();
  for (const group of _SYNS) {
    const norm = group.map((g) => _norm(g));
    for (const w of norm) m.set(w, norm);
  }
  return m;
})();

// Si un token termina en "es" o "s" pa' plural simple, devolver también el singular.
function _morphVariants(tok) {
  const out = new Set([tok]);
  if (tok.length > 4 && tok.endsWith("es")) out.add(tok.slice(0, -2));
  if (tok.length > 3 && tok.endsWith("s")) out.add(tok.slice(0, -1));
  // Inversa: agregar plural simple por si en el contenido aparece plural y se busca singular
  if (tok.length > 3 && !tok.endsWith("s")) {
    out.add(tok + "s");
    out.add(tok + "es");
  }
  return out;
}

function _expandToken(tok) {
  const variants = _morphVariants(tok);
  const expanded = new Set(variants);
  for (const v of variants) {
    const syns = _SYN_INDEX.get(v);
    if (syns) syns.forEach((s) => expanded.add(s));
  }
  return Array.from(expanded);
}

function buildFilter(params) {
  const get = (k) => params instanceof URLSearchParams ? params.get(k) : params[k];
  const q = (get("q") || "").trim();
  const cat = get("categoria") || "";
  const fest = get("festividad") || "";
  const sg = get("sin_gluten") === "true" || get("sin_gluten") === true;
  const tag = _norm(get("tag") || "");

  // Tokenize query: strip accents, split by non-word, drop stopwords and tokens <2 chars.
  const rawTokens = _norm(q)
    .split(/[^\wñ]+/)
    .filter((t) => t && t.length >= 2 && !_STOP.has(t));
  // Cada token se expande a sus variantes (plural/singular + sinónimos).
  const tokenGroups = rawTokens.map(_expandToken);

  return (r) => {
    if (cat && r.categoria !== cat) return false;
    if (fest && r.festividad !== fest) return false;
    if (sg && !r.sin_gluten) return false;
    if (tag) {
      const tags = (r.tags || []).map(_norm);
      if (!tags.some((t) => t.includes(tag))) return false;
    }
    if (tokenGroups.length) {
      // Exclusión selectiva: si TODOS los tokens del query están en
      // _search_exclude_if_alone de la receta, la excluimos. Esto sirve pa'
      // recetas como "Pollo Ganso" (que ES carne, no ave) — no queremos que
      // aparezca al buscar "pollo" solo, pero SÍ al buscar "pollo ganso".
      if (Array.isArray(r._search_exclude_if_alone) && r._search_exclude_if_alone.length) {
        const excludes = new Set();
        for (const t of r._search_exclude_if_alone) {
          _expandToken(_norm(t)).forEach((v) => excludes.add(v));
        }
        const allExcluded = tokenGroups.every((group) => group.some((v) => excludes.has(v)));
        if (allExcluded) return false;
      }
      const hay = _norm([
        r.nombre, r.descripcion_corta, r.categoria, r.origen, r.festividad, r.notas,
        ...(r.ingredientes || []).map(String),
        ...(r.tags || []).map(String),
      ].join(" "));
      // Cada GRUPO de variantes basta con que UNO matchee como substring.
      for (const group of tokenGroups) {
        if (!group.some((v) => hay.includes(v))) return false;
      }
    }
    return true;
  };
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// LocalStorage helpers for menus (per-member)
function lsKey(member, key) { return `recetario_${member}_${key}`; }

function getMenusForMember(member) {
  try {
    const raw = localStorage.getItem(lsKey(member, "menus"));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveMenusForMember(member, menus) {
  localStorage.setItem(lsKey(member, "menus"), JSON.stringify(menus));
}

// Mock axios-like API for use in pages that already use api().get/post/...
function currentMember() {
  return localStorage.getItem("recetario_member") || "papa";
}

async function handle(method, url, body, opts = {}) {
  const path = url.replace(/^\//, "").split("?")[0];
  const search = url.includes("?") ? new URLSearchParams(url.split("?")[1]) : new URLSearchParams(opts.params || {});

  // ============ RECIPES ============
  if (method === "GET" && path === "recipes") {
    const all = await getRecipes();
    const filt = buildFilter(search);
    const filtered = all.filter(filt);
    const limit = parseInt(search.get("limit") || "0");
    const offset = parseInt(search.get("offset") || "0");
    const sliced = limit ? filtered.slice(offset, offset + limit) : filtered;
    return { data: { count: filtered.length, recipes: sliced } };
  }

  const recipeMatch = path.match(/^recipes\/([^/]+)$/);
  if (method === "GET" && recipeMatch) {
    const slug = decodeURIComponent(recipeMatch[1]);
    const all = await getRecipes();
    const r = all.find((x) => x.slug === slug);
    if (!r) throw Object.assign(new Error("Receta no encontrada"), { response: { status: 404 } });
    return { data: r };
  }

  const randomMatch = path.match(/^recipes\/random\/(\d+)$/);
  if (method === "GET" && randomMatch) {
    const n = parseInt(randomMatch[1]);
    const all = await getRecipes();
    return { data: { recipes: shuffle(all).slice(0, n) } };
  }

  // POST /recipes — guardar receta nueva del usuario en localStorage por miembro
  if (method === "POST" && path === "recipes") {
    const member = currentMember();
    const key = `recetario_user_recipes_${member}`;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || "[]"); } catch { list = []; }
    const nombre = (body?.nombre || "").trim();
    if (!nombre) throw Object.assign(new Error("Falta el nombre"), { response: { status: 400, data: { detail: "Falta el nombre" } } });
    const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
    let slug = slugify(nombre) || `receta-${Date.now()}`;
    const all = [...list, ...(_cache.recipes || [])];
    if (all.some((r) => r.slug === slug)) slug = `${slug}-${Math.random().toString(36).slice(2, 6)}`;
    const newR = {
      slug,
      nombre,
      descripcion_corta: body?.descripcion_corta || "",
      categoria: body?.categoria || "Platos Caseros y Vegetarianos",
      festividad: body?.festividad || null,
      origen: body?.origen || null,
      porciones: body?.porciones || "",
      tiempo_preparacion: body?.tiempo_preparacion || "",
      tiempo_coccion: body?.tiempo_coccion || "",
      dificultad: body?.dificultad || "Media",
      ingredientes: Array.isArray(body?.ingredientes) ? body.ingredientes : [],
      instrucciones: Array.isArray(body?.instrucciones) ? body.instrucciones : [],
      notas: body?.notas || "",
      photo: body?.photo_data_url || null,
      _user_added: true,
      _added_by: member,
      _added_at: new Date().toISOString(),
    };
    list.push(newR);
    localStorage.setItem(key, JSON.stringify(list));
    return { data: newR };
  }

  // ============ META ============
  if (method === "GET" && path === "meta") {
    const meta = await getMeta();
    return { data: meta };
  }

  // ============ CAPSULES ============
  if (method === "GET" && path === "capsules/random") {
    const caps = await getCapsules();
    if (!caps.length) return { data: null };
    return { data: caps[Math.floor(Math.random() * caps.length)] };
  }

  // ============ MENUS (localStorage per member) ============
  if (method === "GET" && path === "menus") {
    return { data: getMenusForMember(currentMember()) };
  }
  if (method === "POST" && path === "menus") {
    const member = currentMember();
    const menus = getMenusForMember(member);
    const newMenu = {
      id: `menu_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: body?.name || "Sin nombre",
      recipe_slugs: body?.recipe_slugs || [],
      created_at: new Date().toISOString(),
    };
    menus.push(newMenu);
    saveMenusForMember(member, menus);
    return { data: newMenu };
  }
  const menuMatch = path.match(/^menus\/([^/]+)$/);
  if (menuMatch) {
    const id = menuMatch[1];
    const member = currentMember();
    const menus = getMenusForMember(member);
    if (method === "GET") {
      const m = menus.find((x) => x.id === id);
      if (!m) throw Object.assign(new Error("Menú no encontrado"), { response: { status: 404 } });
      // Embed recipes for the detail view
      const recipes = await getRecipes();
      const items = m.recipe_slugs.map((s) => recipes.find((r) => r.slug === s)).filter(Boolean);
      return { data: { ...m, recipes: items } };
    }
    if (method === "PATCH") {
      const idx = menus.findIndex((x) => x.id === id);
      if (idx < 0) throw Object.assign(new Error("Menú no encontrado"), { response: { status: 404 } });
      menus[idx] = { ...menus[idx], ...body };
      saveMenusForMember(member, menus);
      return { data: menus[idx] };
    }
    if (method === "DELETE") {
      saveMenusForMember(member, menus.filter((x) => x.id !== id));
      return { data: { ok: true } };
    }
  }
  const shoppingMatch = path.match(/^menus\/([^/]+)\/shopping-list$/);
  if (method === "GET" && shoppingMatch) {
    const id = shoppingMatch[1];
    const menus = getMenusForMember(currentMember());
    const m = menus.find((x) => x.id === id);
    if (!m) throw Object.assign(new Error("Menú no encontrado"), { response: { status: 404 } });
    const recipes = await getRecipes();
    const recItems = m.recipe_slugs.map((s) => recipes.find((r) => r.slug === s)).filter(Boolean);

    // Categorize ingredients into sections (simple keyword-based, Spanish/Chilean)
    const SECTIONS = [
      { name: "Verdulería y frutas", kw: /palta|tomate|cebolla|ajo|lechuga|choclo|zapallo|zanahoria|pimentón|pimiento|papa|papas|apio|cilantro|perejil|albahaca|ciboulette|jeng[ie]bre|champi[ñn]on|esp[ai]nac|brócoli|brocoli|coliflor|repollo|pepino|rabanito|betarraga|berenjena|zucchini|porotos\s*verdes|arveja|choclo|ma[ií]z|champ|hongos|lim[oó]n|naranja|manzana|pl[aá]tano|pera|frutilla|frambuesa|ar[aá]ndano|kiwi|uva|durazno|damasco|cereza|sand[ií]a|mel[oó]n|piña|mango|palta/i },
      { name: "Carnicería y pescadería", kw: /carne|posta|asado|lomo|filete|pollo|pavo|cerdo|chuleta|costilla|molida|molido|mechada|pernil|tocino|panceta|jam[oó]n|salchicha|chorizo|salm[oó]n|merluza|reineta|congrio|atún|atun|camar[oó]n|jaiba|pulpo|calamar|machas|locos|ostiones|mariscos|pescado/i },
      { name: "Lácteos y huevos", kw: /leche|crema|yog[uú]r|queso|mantequilla|margarina|huevo|huevos|ricotta|mozzarella|parmesano|provolone|gouda|cheddar|quark/i },
      { name: "Panadería y harinas", kw: /harina|pan\b|pan rallado|migas|hojaldre|masa\s*(filo|brisa|hojaldre)|tortilla|levadura|tort[ií]n/i },
      { name: "Despensa", kw: /az[uú]car|sal\b|aceite|vinagre|salsa|mostaza|mayonesa|kétchup|miel|chocolate|cacao|coco rallado|coco|nueces|almendras|man[ií]|pistacho|pasas|d[aá]tiles|arroz|fideos|pasta|s[eé]mola|polenta|quinoa|lentejas|garbanzos|porotos|tofu|caldo|cubo|t[eé]\b|caf[eé]|gelatina|maicena|polvo de hornear|bicarbonato/i },
      { name: "Especias y condimentos", kw: /pimienta|comino|or[eé]gano|tomillo|romero|laurel|paprika|p[aá]prika|merk[eé]n|aj[ií]\b|aj[ií]\s|canela|nuez moscada|cardamomo|jengibre molido|curry|cúrcuma|curcuma|s[eé]samo|sesamo|amapola|chili|cayena|achiote|ciboulette|piment[oó]n ahum/i },
      { name: "Bebidas y vinos", kw: /vino|cerveza|champ[aá]?gne|espumante|whisky|ron|vodka|pisco|jugo|bebida|coca[\s-]?cola|sprite|agua mineral|leche\s*de\s*coco|leche\s*de\s*almendra/i },
      { name: "Congelados y otros", kw: /helado|hielo|congelad/i },
    ];

    function classify(text) {
      for (const sec of SECTIONS) if (sec.kw.test(text)) return sec.name;
      return "Otros";
    }

    // Aggregate ingredients across all recipes
    const items = [];
    const seen = new Set();
    for (const r of recItems) {
      for (const i of (r.ingredientes || [])) {
        const text = String(i).trim();
        if (!text || text.endsWith(":")) continue; // skip section headers
        // de-dupe by lowercase normalized text + recipe
        const key = `${text.toLowerCase()}|${r.slug}`;
        if (seen.has(key)) continue;
        seen.add(key);
        items.push({
          texto: text,
          seccion: classify(text),
          recipe: r.nombre,
          recipe_slug: r.slug,
        });
      }
    }
    // Sort by section so groupBy in the UI keeps order
    const SECTION_ORDER = [...SECTIONS.map((s) => s.name), "Otros"];
    items.sort((a, b) => SECTION_ORDER.indexOf(a.seccion) - SECTION_ORDER.indexOf(b.seccion));

    return {
      data: {
        menu_id: m.id,
        menu_name: m.name,
        recipe_count: recItems.length,
        items,
      },
    };
  }

  // ============ TTS STATUS ============
  if (method === "GET" && path === "tts/status") {
    return { data: { ready: true, is_pvc: true, voice_id: "static" } };
  }

  // ============ PREGUNTALE A PAPI (live, calls Worker) ============
  if (method === "POST" && path === "ask-papi") {
    // URL del Worker: env var en producción, fallback hardcoded al Worker
    // conocido para que funcione incluso si la env var no se define.
    const workerUrl = process.env.REACT_APP_PAPI_WORKER_URL
      || "https://preguntale-a-papi.guendelman.workers.dev/ask-papi";
    const member = currentMember();
    const guest_name = member === "invitado"
      ? (localStorage.getItem("recetario_guest_name") || "").trim()
      : "";
    const res = await fetch(workerUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, member, guest_name }),
    });
    if (!res.ok) throw new Error("Papi no pudo responder");
    return { data: await res.json() };
  }

  // Unknown endpoint — log so we know what's missing
  console.warn(`staticApi: unhandled ${method} ${url}`);
  throw Object.assign(new Error("Endpoint no soportado en modo estático"), {
    response: { status: 501 },
  });
}

export const staticApi = {
  get: (url, opts) => handle("GET", url, undefined, opts),
  post: (url, body, opts) => handle("POST", url, body, opts),
  patch: (url, body, opts) => handle("PATCH", url, body, opts),
  delete: (url, opts) => handle("DELETE", url, undefined, opts),
};

// URL helpers for static media
export const audioUrl = (slug, type, n) => {
  const base = process.env.REACT_APP_AUDIO_BASE_URL || "/audio";
  if (type === "intro") return `${base}/${slug}/intro.mp3`;
  if (type === "step") return `${base}/${slug}/step_${n}.mp3`;
  if (type === "farewell") return `${base}/${slug}/farewell_${n}.mp3`; // n = nick
  if (type === "capsule") return `${base}/capsules/capsule_${slug}.mp3`; // slug = id
  if (type === "welcome") return `${base}/welcome/welcome_${slug}.mp3`; // slug = nick (full — primera vez)
  if (type === "welcome_short") return `${base}/welcome/welcome_${slug}_short.mp3`; // slug = nick (saludo corto para vueltas siguientes)
  return "";
};

export const photoUrl = (slug) => `/photos/recipes/${slug}.jpg`;
